/**
 * The order behind <x-insights.sortable-header> (D173 of documentation/pr-plans/insights-simplify-plan.md): what a
 * press does to a table's order, what the header then carries, how the rows are put in it and what is said, in the
 * activity table's rules (insights-activity-table.js compareRows(), toolbar.js sortBy()), so a table sorting with
 * the shared header sorts as the activity tables do. An order is { key, direction }, direction 'asc' or 'desc'.
 */

export const ASCENDING = 'asc';

export const DESCENDING = 'desc';

const collator = new Intl.Collator(undefined, { sensitivity: 'base', numeric: true });

/**
 * The order after a press on `key`: its own order flipped when it is the one on, else `key` in `first`, the way
 * its column reads best first (largest first for a figure, A to Z for a name, top of Google first for a position).
 */
export function nextSort(sort, key, first) {
    if (sort?.key === key) return { key, direction: sort.direction === ASCENDING ? DESCENDING : ASCENDING };

    return { key, direction: first };
}

/** A header's aria-sort: the order's way on the sorted column, null on every other. */
export function ariaSort(sort, key) {
    if (!sort || sort.key !== key) return null;

    return sort.direction === ASCENDING ? 'ascending' : 'descending';
}

/** The arrow beside a header's words, which only the sorted column shows. */
export function sortArrow(sort, key) {
    if (!sort || sort.key !== key) return '';

    return sort.direction === ASCENDING ? '↑' : '↓';
}

/**
 * The rows in the order: `valueOf(row)` is what the column compares, a number, or a string for a column of
 * names (compared as people read them, whatever the case and with numbers by value). A row with nothing to
 * compare goes last either way, so a press never brings the empty rows to the top; ties keep the order the
 * rows came in, so a sort is stable and a list the server ranked stays ranked within a tie. A new list.
 */
export function sortRows(rows, sort, valueOf) {
    if (!sort) return [...rows];

    const ranked = rows.map((row, index) => ({ row, index, value: valueOf(row) ?? null }));

    ranked.sort((a, b) => {
        if (a.value === null || b.value === null) {
            return a.value === b.value ? a.index - b.index : (a.value === null ? 1 : -1);
        }

        const result = typeof a.value === 'string' || typeof b.value === 'string'
            ? collator.compare(String(a.value), String(b.value))
            : a.value - b.value;

        if (result === 0) return a.index - b.index;

        return sort.direction === DESCENDING ? -result : result;
    });

    return ranked.map((entry) => entry.row);
}

/**
 * What a screen reader hears after a press, in the activity table's words: "Sorted by Impressions, highest
 * first." `directions` is activity_table.direction, whose words a column's `kind` picks (text: A to Z,
 * position: top of Google first, stage: ready to buy first, as the activity table's toolbar.js says a search
 * intent column; any other: highest or lowest first).
 */
export function sortSentence(template, directions, label, direction, kind = null) {
    const prefix = { text: 'text_', position: 'position_', stage: 'stage_' }[kind] ?? '';

    return String(template ?? '').replace(':column', label).replace(':direction', directions?.[prefix + direction] ?? '');
}
